import { useCallback, useEffect, useRef, useState } from "react";

export type PageInfo = {
  title?: string;
  cover?: string;
  link?: string;
  gameId?: string;
};

type StoredPayload = {
  page: PageInfo;
  createdAt: number;
  expiredAt: number;
};

export type UseBackToGameReturn = {
  show: boolean;
  data: PageInfo | null;
  dismiss: () => void;
};

const LAST_GAME_KEY = "last-game";

const safeJSONParse = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const readStored = (): StoredPayload | null => {
  return safeJSONParse<StoredPayload>(
    window.localStorage.getItem(LAST_GAME_KEY),
  );
};

const writeStored = (payload: StoredPayload) => {
  window.localStorage.setItem(LAST_GAME_KEY, JSON.stringify(payload));
};

const clearStored = () => {
  window.localStorage.removeItem(LAST_GAME_KEY);
};

export const saveLastGame = (page: PageInfo, ttlHours: number = 72) => {
  const createdAt = Date.now();
  const expiredAt = createdAt + ttlHours * 60 * 60 * 1000;
  writeStored({ page, createdAt, expiredAt });
};

export const clearLastGame = () => {
  clearStored();
};

export const useBackToGame = (): UseBackToGameReturn => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState<PageInfo | null>(null);
  const decidedRef = useRef(false);

  const evaluate = useCallback(() => {
    if (decidedRef.current) {
      return;
    }

    const stored = readStored();
    if (!stored) {
      setShow(false);
      setData(null);
      return;
    }

    const isFresh = Date.now() <= stored.expiredAt;

    if (!isFresh) {
      clearStored();
      setShow(false);
      setData(null);
      return;
    }

    decidedRef.current = true;
    if (stored.page.cover && stored.page.link && stored.page.title) {
      setData(stored.page);
      setShow(true);
    }
  }, []);

  useEffect(() => {
    evaluate();

    const onStorage = (e: StorageEvent) => {
      if (e.storageArea === window.localStorage && e.key === LAST_GAME_KEY) {
        decidedRef.current = false;
        evaluate();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [evaluate]);

  const dismiss = useCallback(() => {
    clearStored();
    setShow(false);
    setData(null);
  }, []);

  return { show, data, dismiss };
};
